import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { api, extractApiError } from '@/lib/api';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Backend /auth/verify-email returns 204 No Content — there's no body to read.
  // We treat HTTP success as success.
  const mutation = useMutation({
    mutationFn: async (verifyToken: string) => {
      await api.post('/auth/verify-email', { token: verifyToken });
    },
  });

  const mutate = mutation.mutate;
  const firedFor = useRef<string | null>(null);
  useEffect(() => {
    if (token && firedFor.current !== token) {
      firedFor.current = token;
      mutate(token);
    }
  }, [token, mutate]);

  useEffect(() => {
    if (mutation.isSuccess) {
      const t = setTimeout(() => {
        navigate(isAuthenticated ? '/dashboard' : '/sign-in', { replace: true });
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [mutation.isSuccess, isAuthenticated, navigate]);

  if (!token) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Verify email</CardTitle>
          <CardDescription>This link is missing a verification token.</CardDescription>
        </CardHeader>
        <CardFooter>
          <Button asChild variant="outline" className="w-full">
            <Link to="/sign-in">Back to sign in</Link>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  if (mutation.isPending || mutation.isIdle) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Verifying your email…</CardTitle>
          <CardDescription>Hold on while we confirm your token.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (mutation.isSuccess) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Email verified
          </CardTitle>
          <CardDescription>
            Thanks — your email is confirmed. Redirecting you in a moment.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button asChild className="w-full">
            <Link to={isAuthenticated ? '/dashboard' : '/sign-in'}>
              {isAuthenticated ? 'Go to dashboard' : 'Sign in'}
            </Link>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  const apiErr = extractApiError(mutation.error);
  const message =
    apiErr?.code === 'auth.verify_token_invalid'
      ? 'This verification link is invalid, expired, or already used.'
      : apiErr?.message || 'Verification failed. Please try again.';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <XCircle className="h-5 w-5 text-destructive" />
          Verification failed
        </CardTitle>
        <CardDescription>{message}</CardDescription>
      </CardHeader>
      <CardFooter>
        <Button asChild variant="outline" className="w-full">
          <Link to="/sign-in">Back to sign in</Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
